import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateEvent } from '../api/owner'
import { dateBounds, formatEventDate, isSaneDate } from '../lib/format'
import { Monogram } from '../components/Monogram'
import { useEvent } from './eventContext'
import { EventScreen, SaveBar } from './components/EventScreen'
import { LandingPhotos } from './components/LandingPhotos'

/** Zoveel tekens past de welkomsttekst; de teller in ronde 20T zegt 140. */
const maxWelcome = 140

/**
 * Ronde 20H en 20T — eventinfo bewerken.
 *
 * Op desktop staat er een voorbeeld van de gastenlanding naast, zodat je ziet
 * wat je aan het veranderen bent zonder het echt te openen.
 */
export function EventInfoPage() {
  const { subject, refresh } = useEvent()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    coupleNames: subject.coupleNames,
    eventDate: subject.eventDateUtc ? subject.eventDateUtc.slice(0, 10) : '',
    venue: subject.venue ?? '',
    address: subject.address ?? '',
    welcomeMessage: subject.welcomeMessage ?? '',
  })

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const original = {
    coupleNames: subject.coupleNames,
    eventDate: subject.eventDateUtc ? subject.eventDateUtc.slice(0, 10) : '',
    venue: subject.venue ?? '',
    address: subject.address ?? '',
    welcomeMessage: subject.welcomeMessage ?? '',
  }

  const dirty = (Object.keys(form) as (keyof typeof form)[]).some(
    (key) => form[key] !== original[key],
  )

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  async function save() {
    setSaving(true)
    setError(null)

    try {
      await updateEvent(subject.id, {
        coupleNames: form.coupleNames.trim(),
        venue: form.venue.trim(),
        address: form.address.trim(),
        welcomeMessage: form.welcomeMessage.trim(),
        eventDateUtc: form.eventDate ? new Date(form.eventDate).toISOString() : undefined,
        clearEventDate: form.eventDate === '',
      })

      await refresh()
      await queryClient.invalidateQueries({ queryKey: ['owner', 'events'] })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opslaan lukte niet.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <EventScreen
      title="Eventinfo"
      eventTitle={subject.title}
      desktopSubtitle="Namen en welkomsttekst staan op de landingspagina."
      footer={
        <>
          {error && (
            <p role="alert" className="text-accent mb-3 text-sm">
              {error}
            </p>
          )}
          <SaveBar
            saving={saving}
            dirty={dirty}
            onCancel={() => setForm(original)}
            onSave={() => void save()}
          />
        </>
      }
    >
      <div className="lg:flex lg:gap-10">
        <div className="flex flex-col gap-4.5 lg:w-120 lg:gap-6.5">
          <Field label="Namen">
            <input
              value={form.coupleNames}
              onChange={(event) => set('coupleNames')(event.target.value)}
              maxLength={200}
              className="font-display rounded-row border-line-card bg-surface focus:border-accent w-full border px-4 py-3.75 text-2xl outline-none lg:px-4.5 lg:py-4 lg:text-[26px]"
            />
          </Field>

          <div className="flex flex-col gap-4.5 lg:flex-row lg:gap-4">
            <div className="lg:flex-1">
              <Field label="Datum">
                <input
                  type="date"
                  value={form.eventDate}
                  min={dateBounds.min}
                  max={dateBounds.max}
                  onChange={(event) => {
                    if (isSaneDate(event.target.value)) {
                      set('eventDate')(event.target.value)
                    }
                  }}
                  className="rounded-row border-line-card bg-surface focus:border-accent w-full border px-4 py-3.75 text-base outline-none lg:px-4.5 lg:py-4"
                />
              </Field>
            </div>

            <div className="lg:flex-1">
              <Field label="Locatie">
                <input
                  value={form.venue}
                  onChange={(event) => set('venue')(event.target.value)}
                  maxLength={160}
                  placeholder="Hoeve De Linde"
                  className="rounded-row border-line-card bg-surface placeholder:text-ink-ghost focus:border-accent w-full border px-4 py-3.75 text-base outline-none lg:px-4.5 lg:py-4"
                />
              </Field>
            </div>
          </div>

          <Field label="Adres">
            <input
              value={form.address}
              onChange={(event) => set('address')(event.target.value)}
              maxLength={240}
              placeholder="Lindestraat 14, Gent"
              className="rounded-row border-line-card bg-surface placeholder:text-ink-ghost focus:border-accent w-full border px-4 py-3.75 text-base outline-none lg:px-4.5 lg:py-4"
            />
          </Field>

          <Field label="Welkomsttekst">
            <textarea
              value={form.welcomeMessage}
              onChange={(event) => set('welcomeMessage')(event.target.value.slice(0, maxWelcome))}
              rows={3}
              className="font-display rounded-row border-line-card bg-surface text-ink-muted focus:border-accent w-full resize-none border px-4 py-3.75 text-[19px] leading-[1.45] italic outline-none lg:min-h-21 lg:px-4.5 lg:py-4 lg:text-[21px]"
            />
            <div className="text-ink-faint mt-2 flex justify-between text-[13px]">
              <span>Onder de namen</span>
              <span>
                {form.welcomeMessage.length} / {maxWelcome}
              </span>
            </div>
          </Field>

          <LandingPhotos eventId={subject.id} photos={subject.photos} onChanged={refresh} />
        </div>

        {/* Alleen desktop: het voorbeeld uit ronde 20T. */}
        <div className="hidden lg:block">
          <p className="text-ink-faint mb-3.5 text-xs tracking-[0.18em] uppercase">Voorbeeld</p>
          <LandingPreview
            coupleNames={form.coupleNames}
            date={form.eventDate}
            venue={form.venue}
            welcome={form.welcomeMessage}
            cover={subject.photos.at(0)?.url}
            accent={subject.accentColor}
          />
        </div>
      </div>
    </EventScreen>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-ink-faint mb-2.5 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
        {label}
      </p>
      {children}
    </div>
  )
}

/** Het telefoontje naast het formulier: hoe de gastenlanding eruit komt te zien. */
function LandingPreview({
  coupleNames,
  date,
  venue,
  welcome,
  cover,
  accent,
}: {
  coupleNames: string
  date: string
  venue: string
  welcome: string
  cover?: string
  accent: string
}) {
  const meta = [date ? formatEventDate(date) : null, venue || null].filter(Boolean).join(' · ')

  return (
    <div className="border-line-card bg-surface flex h-105 w-65 flex-col items-center overflow-hidden rounded-[20px] border text-center shadow-[0_10px_26px_rgba(42,35,32,0.08)]">
      <div className="placeholder-warm h-37.5 w-full shrink-0">
        {cover && <img src={cover} alt="" className="h-full w-full object-cover" />}
      </div>

      <div className="-mt-5.5">
        <Monogram coupleNames={coupleNames || '?'} size={44} />
      </div>

      <div className="px-5 pt-3.5">
        <p className="font-display text-[26px] leading-[1.05]">{coupleNames || 'Jullie namen'}</p>
        {meta && (
          <p className="text-ink-faint mt-2 text-[9px] tracking-[0.2em] uppercase">{meta}</p>
        )}
        <p className="font-display text-ink-muted mt-3.5 line-clamp-4 text-sm leading-[1.45] italic">
          {welcome}
        </p>
      </div>

      <div className="flex-1" />

      <div className="w-full px-5 pb-5">
        <div
          style={{ background: accent }}
          className="text-accent-ink flex h-9.5 items-center justify-center rounded-full text-[13px] font-semibold"
        >
          Deel je foto’s
        </div>
      </div>
    </div>
  )
}
